/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { memo } from 'react';
import Image, { akamaiLoader } from '@/components/NextImage';
import cx from 'classnames';
import PlayArrowIcon from '@/public/images/play-arrow.svg';
import ThreeSixtyIcon from '@/public/images/360_Icon.svg';
import { defaultVideoThumbnail } from '@/constants/images/defaultVideoThumbnail';
import type { CarouselItem } from './types';
import styles from './styles.module.scss';
import ThumbnailOverlay from './ThumbnailOverlay';

type MemoizedThumbnailProps = {
  thumbnailItem: CarouselItem;
  thumbIndex: number;
  isSelected: boolean;
  isSeeMoreButton: boolean;
  ariaLabel: string;
  onMouseEnter: () => void;
  onClick: () => void;
  thumbnailRef: (element: HTMLLIElement | null) => void;
};

const MemoizedThumbnailComponent = ({
  thumbnailItem,
  thumbIndex,
  isSelected,
  isSeeMoreButton,
  ariaLabel,
  onMouseEnter,
  onClick,
  thumbnailRef,
}: MemoizedThumbnailProps) => {
  const { type, thumbnail } = thumbnailItem;
  const isVideo = type === 'video';
  const is360 = type === 'spin360';
  const isInvalidVideoThumbnail = isVideo && (!thumbnail || thumbnail === 'null');
  const thumbnailStyle = `${styles.thumbnailBox} ${styles.inlineThumbnailBox}`;

  return (
    <li ref={thumbnailRef} className={thumbnailStyle} key={`${thumbnail}-${thumbIndex}`}>
      <button
        className={cx(styles.thumbnailButtonV2, {
          [styles.thumbnailButtonSelectedV2]: !isSeeMoreButton && isSelected,
        })}
        onMouseEnter={onMouseEnter}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        <Image
          height={60}
          width={60}
          src={isInvalidVideoThumbnail ? defaultVideoThumbnail.src : thumbnail}
          alt=""
          loader={akamaiLoader}
          unoptimized={false}
          className={styles.thumbnailImage}
          data-testid={`thumbnail-image-${thumbIndex}`}
          itemProp="image"
        />
        {(isVideo || is360) && !isSeeMoreButton && (
          <ThumbnailOverlay
            image={(isVideo ? PlayArrowIcon : ThreeSixtyIcon) as React.ElementType<SVGElement>}
            isSelected={isSelected}
          />
        )}
        {isSeeMoreButton && <ThumbnailOverlay text={ariaLabel} />}
      </button>
    </li>
  );
};

export const MemoizedThumbnail = memo(MemoizedThumbnailComponent, (prevProps, nextProps) => {
  return (
    prevProps.thumbnailItem.thumbnail === nextProps.thumbnailItem.thumbnail &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isSeeMoreButton === nextProps.isSeeMoreButton &&
    prevProps.thumbIndex === nextProps.thumbIndex
  );
});
